import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { HelpCircle, ChevronDown } from 'lucide-react';

const FAQ_ITEMS = [
  {
    question: 'Can Sortio organize vendor invoices into per-vendor folders automatically?',
    answer:
      "Yes. Point Sortio at the folder where vendor invoices land (OneDrive, Dropbox, SharePoint, or a local drive) and it reads each PDF or scan, identifies the vendor, and routes the file to that vendor's folder. If a vendor folder does not exist yet, Sortio can create it. Bookkeepers at construction and trucking firms commonly run this on 3,000-plus file backlogs in a single sort.",
  },
  {
    question: 'How does Sortio rename files? Can I use a YYYYMMDD, Vendor, Amount pattern?',
    answer:
      "That exact pattern is one of the most popular prompts. A real customer prompt is 'YYYYMMDD - Vendor Name - $ Value', which produces filenames like '20250412 - Aster Construction - $4,820.50.pdf'. You write the pattern once in plain English, preview the proposed renames, and apply. Sortio also keeps an undo log of every move.",
  },
  {
    question: 'Will Sortio dedup my messy vendor folders like Aster vs Aster Inc vs Aster Construction?',
    answer:
      "Yes. Sortio includes a folder validation step that flags near-duplicate vendor folders, including common typos like 'Speadsheets' vs 'Spreadsheets' or 'Aster' vs 'Aster Drive' vs 'Aster Condo PSA'. You pick the canonical folder and Sortio consolidates the files. This is the difference between thousands of stranded invoices and one clean vendor index.",
  },
  {
    question: 'Does Sortio work with QuickBooks Online or Xero?',
    answer:
      "Sortio is filesystem-transparent, so it organizes the source documents and exports that flow into and out of QuickBooks Online, QuickBooks Desktop, and Xero. Files renamed with vendor, date, and amount drop straight into your accounting workflow without remapping. Sortio does not push journal entries into QuickBooks or Xero, it organizes the documents that support them.",
  },
  {
    question: 'How does Sortio handle a multi-year payroll archive?',
    answer:
      "Sortio recognizes payroll registers, pay stubs, and tax filings, then groups them by year and pay period. A common layout is Payroll / 2024 / 2024-04 - Pay Period 8 / [files], but you can describe whatever structure your firm uses. Cleaning up a decade of payroll that lives in nested OneDrive folders becomes a single sort run.",
  },
  {
    question: 'Does Sortio work across OneDrive folders shared by multiple Windows users?',
    answer:
      "Yes. Bookkeepers often inherit OneDrive paths that cross several Windows user profiles, with overlapping copies of the same vendor invoices. Sortio reads files in place, detects duplicates by content (not just filename), and routes the canonical copy to the right vendor or project folder. It handles long Windows paths and OneDrive on-demand placeholders.",
  },
  {
    question: 'Is Sortio safe to use for client financial data and employee payroll?',
    answer:
      "Sortio runs on your Mac or PC and reads files locally. In Offline mode with a local LLM (Ollama), or with Bring Your Own Key against your own provider account, vendor banking details and employee pay information stay on your machine and do not pass through a third party. By default Sortio uses our cloud (OpenAI) to plan the sort, which sends filenames and folder paths; the contents of an invoice are sent only if you enable a content-based feature like AI rename by content. For bookkeepers handling sensitive financials, running Offline or BYOK helps keep that data on your own machine.",
  },
  {
    question: 'Can I organize construction project invoices by job number or project?',
    answer:
      "Yes. If your invoices contain a job number, project code, or property reference, Sortio extracts it and routes files into the matching project folder. A common construction layout is Projects / Aster Condo PSA / Invoices / 2025 / [files]. The same pattern works for general contractors, subcontractors, and developers tracking dozens of active jobs at once.",
  },
  {
    question: 'Does Sortio handle trucking route documents, fuel receipts, and driver logs?',
    answer:
      "Trucking back offices use Sortio to file bills of lading, fuel receipts, IFTA paperwork, and driver logs by date, route, or driver. A typical layout is Trucking / 2025 / Driver Name / [files] or Trucking / Routes / [route code] / [files]. You describe the structure once and Sortio applies it across the multi-year backlog.",
  },
  {
    question: 'Can Sortio organize records for a special district or municipal back office?',
    answer:
      "Yes. Operations managers at special districts use Sortio to file board packets, lift station logs, CDPHE water-quality permits, and mill levy certifications by year and document type. Public-sector record retention is rule-based, and Sortio lets you encode those rules in plain English so the filing matches the policy.",
  },
  {
    question: 'Does Sortio help with records retention policy compliance?',
    answer:
      "Sortio organizes files so retention windows are easy to enforce, by sorting documents into year folders or retention-bucket folders that match your policy. It does not delete files for you, the decision to purge stays with the records officer. The audit log captures every rename and move with source path, destination, and confidence score.",
  },
  {
    question: 'How big a batch can Sortio AI sort process at once?',
    answer:
      "Pro handles up to 5,000 files per AI sort, which covers most monthly vendor invoice runs and a typical year of payroll. For drives in the tens of thousands, we recommend the AI Rule Builder, which generates a rule set from a sample and then applies it locally to the full drive without touching your AI allowance. Customers regularly sort 3,000-plus file backlogs in a single run.",
  },
  {
    question: 'How much does Sortio cost for a bookkeeper or back office team?',
    answer:
      "Pro is $14.99 per month or $99 per year and is what most solo bookkeepers and AP admins use. Team is $29 per seat per month with shared rules, an admin console, and centralized model policy, which is the right fit when the whole back office is ready to standardize vendor filing. There is also a free tier with a one-time AI trial allowance if you want to try it on one month of invoices first.",
  },
  {
    question: 'Does Sortio replace my bookkeeper or AP clerk?',
    answer:
      "No. Sortio removes the file-shuffling and naming work so your bookkeeper or AP clerk spends their time on reconciliation, vendor questions, and month-end close instead of clicking through OneDrive looking for last quarter's invoices. Most teams report getting back several hours per week per person, which compounds across a multi-year backlog.",
  },
];

const FaqItem = ({ item, index, isOpen, onToggle }) => {
  return (
    <div className="bg-apple-800/40 backdrop-blur-sm rounded-2xl border border-apple-700/50 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${index}`}
        className="w-full text-left p-5 sm:p-6 flex items-start justify-between gap-4 focus:outline-none focus:ring-2 focus:ring-sortio-primary/40 rounded-2xl"
      >
        <h3 className="text-base sm:text-lg font-medium text-apple-50 leading-snug">
          {item.question}
        </h3>
        <ChevronDown
          className={`flex-shrink-0 text-sortio-primary transition-transform duration-200 mt-1 ${
            isOpen ? 'rotate-180' : ''
          }`}
          size={20}
        />
      </button>
      {isOpen && (
        <div id={`faq-answer-${index}`} className="px-5 sm:px-6 pb-6 -mt-1">
          <p className="text-sm sm:text-base text-apple-200 leading-relaxed">{item.answer}</p>
        </div>
      )}
    </div>
  );
};

const SmallBusinessFAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <section id="faq" className="my-20">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-sortio-primary/15 border border-sortio-primary/30 mb-4">
          <HelpCircle className="text-sortio-primary" size={22} />
        </div>
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50">
          Frequently asked questions
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl mx-auto">
          Vendor invoice organization, AP filing automation, payroll archives, and records
          retention for small business back offices.
        </p>
      </div>

      <div className="max-w-3xl mx-auto space-y-3">
        {FAQ_ITEMS.map((item, index) => (
          <FaqItem
            key={index}
            item={item}
            index={index}
            isOpen={openIndex === index}
            onToggle={() => setOpenIndex(openIndex === index ? -1 : index)}
          />
        ))}
      </div>
    </section>
  );
};

export default SmallBusinessFAQ;
